import { useEffect, useState, type FormEvent } from 'react';
import { useAuth } from './AuthProvider';
import { ApiError } from '../lib/api';
import { PasswordInput } from '../components/PasswordInput';
import { TurnstileWidget } from '../components/TurnstileWidget';
import { fieldFromServerError, passwordChecks, refFromSearch, trackSignup } from './signupHelpers';

interface Props {
  /** Llamado para volver al login. */
  onGoToLogin: () => void;
}

/**
 * Self-service signup público (V11 / Day 4 — Bloque A). Crea negocio + admin + 14 días.
 * Hotfix del alta (relay tren de fixes v4 §1bis, revisión de Marketing 08/10):
 *   · sin "stratos"/"workspace": se crea un NEGOCIO;
 *   · sin campo de slug: lo genera el backend del nombre (y se muestra tras el alta);
 *   · referido por `?ref=` + enlace discreto para teclearlo;
 *   · sin casilla de novedades (se ofrece adentro, en Configuración);
 *   · requisitos de la contraseña MIENTRAS se escribe;
 *   · Turnstile administrado (solo pide interacción si la señal es mala);
 *   · medición propia del embudo, sin terceros ni datos personales.
 */
export function SignupScreen({ onGoToLogin }: Props) {
  const { signup, loading } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [acceptedLegal, setAcceptedLegal] = useState(false); // Fase 0 — NO premarcado
  // §1bis.4 — referido: llega por ?ref= (sobrevive al envío) o se teclea tras el enlace.
  const [referralCode, setReferralCode] = useState(() => refFromSearch(window.location.search));
  const [showReferral, setShowReferral] = useState(() => !!refFromSearch(window.location.search));
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const checks = passwordChecks(password);

  useEffect(() => { trackSignup('signup_view'); }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    trackSignup('signup_submit');
    if (!acceptedLegal) {
      trackSignup('signup_validation_error', 'acceptedLegal');
      setError('Debes aceptar los Términos y Condiciones y el Aviso de Privacidad.');
      return;
    }
    if (checks.some((c) => !c.ok)) {
      trackSignup('signup_validation_error', 'password');
      setError('Tu contraseña aún no cumple los requisitos.');
      return;
    }
    try {
      await signup({
        email, password, fullName, tenantName, acceptedLegal,
        // Alta 2 (a): la misma casilla atesta la mayoría de edad (se guarda con la aceptación).
        ageAttested: acceptedLegal,
        ...(referralCode.trim() ? { referralCode: referralCode.trim() } : {}),
        ...(turnstileToken ? { turnstileToken } : {}),
      });
      trackSignup('signup_success');
    } catch (err: unknown) {
      let msg: string;
      if (err instanceof ApiError) {
        msg = Array.isArray(err.body?.message) ? err.body.message.join(' · ') : err.body?.message ?? err.message;
      } else {
        msg = (err as Error)?.message ?? 'Error al crear la cuenta';
      }
      trackSignup('signup_validation_error', fieldFromServerError(msg));
      setError(msg);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#FAFAF7] px-4 py-8">
      <div className="card-soft w-full max-w-md rounded-2xl border border-slate-200/80 bg-white p-6 sm:p-8">
        <div className="mb-6">
          <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">FluidSol</p>
          <h1 className="font-serif text-3xl tracking-tight text-slate-900">Crea tu negocio</h1>
          <p className="mt-1 text-sm text-slate-500">Prueba FluidSol 14 días con tu propio negocio.</p>
        </div>

        <form
          onSubmit={onSubmit}
          onInvalidCapture={(e) => {
            const name = (e.target as HTMLInputElement).name;
            if (name) trackSignup('signup_validation_error', name);
          }}
          className="space-y-4"
        >
          <label className="block">
            <span className="mb-1.5 block text-xs text-slate-600">Tu nombre</span>
            <input className="input" name="fullName" value={fullName} onChange={(e) => setFullName(e.target.value)}
              required minLength={3} autoComplete="name" />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs text-slate-600">Correo</span>
            <input className="input" name="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              required autoComplete="email" inputMode="email" />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs text-slate-600">Contraseña</span>
            <PasswordInput name="password" value={password} onChange={setPassword} required minLength={8} autoComplete="new-password" />
            {password.length > 0 && (
              <ul className="mt-1.5 space-y-0.5 text-[11px]" aria-live="polite">
                {checks.map((c) => (
                  <li key={c.label} className={c.ok ? 'text-emerald-700' : 'text-slate-500'}>
                    {c.ok ? '✓' : '○'} {c.label}
                  </li>
                ))}
              </ul>
            )}
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs text-slate-600">¿Cómo se llama tu negocio?</span>
            <input className="input" name="tenantName" value={tenantName} onChange={(e) => setTenantName(e.target.value)}
              required minLength={3} placeholder="Ej. Panadería La Esperanza" autoComplete="organization" />
          </label>

          {showReferral ? (
            <label className="block">
              <span className="mb-1.5 block text-xs text-slate-600">Código de referido</span>
              <input className="input" name="referralCode" value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())} maxLength={32} autoComplete="off" />
            </label>
          ) : (
            <button type="button" onClick={() => setShowReferral(true)}
              className="text-[12px] text-slate-500 underline decoration-slate-300 underline-offset-2 hover:text-slate-800">
              ¿Tienes un código de referido?
            </button>
          )}

          {/* Fase 0 — aceptación legal obligatoria (NO premarcada; V79 / SIGNUP_LEGAL_DOCS). */}
          <label className="flex items-start gap-2.5 text-[12px] leading-relaxed text-slate-600">
            <input type="checkbox" name="acceptedLegal"
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
              checked={acceptedLegal} onChange={(e) => setAcceptedLegal(e.target.checked)} required />
            <span>
              Soy mayor de 18 años y acepto los{' '}
              <a href="/legal/terminos-y-condiciones" target="_blank" rel="noopener noreferrer"
                className="font-medium text-slate-900 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-900">
                Términos y Condiciones
              </a>{' '}y el{' '}
              <a href="/legal/aviso-de-privacidad" target="_blank" rel="noopener noreferrer"
                className="font-medium text-slate-900 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-900">
                Aviso de Privacidad
              </a>.
            </span>
          </label>

          {error && <div className="alert-over rounded-xl px-3 py-2 text-xs">{error}</div>}

          {/* §1bis.7 — administrado: solo aparece si Cloudflare necesita interacción. */}
          <TurnstileWidget onToken={setTurnstileToken} interactionOnly />

          <button type="submit" disabled={loading || !acceptedLegal}
            title={!acceptedLegal ? 'Acepta los Términos y el Aviso de Privacidad para continuar' : undefined}
            className="w-full rounded-xl bg-slate-900 px-6 py-3 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50">
            {loading ? 'Creando…' : 'Empezar mis 14 días gratis'}
          </button>
          <p className="text-center text-[12px] text-slate-500">Sin tarjeta.</p>

          <button type="button" onClick={onGoToLogin}
            className="block w-full text-center text-xs font-medium text-slate-500 hover:text-slate-900">
            ¿Ya tienes cuenta? Inicia sesión
          </button>
        </form>
      </div>
    </div>
  );
}
